/**
 * LeftShield Admin - Settings Page
 * Platform configuration and feature flags
 */
import React, { useState, useEffect } from 'react';
import {
  Title1,
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Button,
  Input,
  Field,
  Switch,
  Spinner,
  tokens,
  Textarea,
  Divider,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import {
  SettingsRegular,
  ShieldLockRegular,
  AlertRegular,
  SaveRegular,
  ArrowResetRegular,
  CheckmarkCircleRegular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '../services/api';
import type { PlatformSettings, UpdatePlatformSettingsRequest } from '../types';

export const SettingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<PlatformSettings | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const {
    data: settings,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['platformSettings'],
    queryFn: () => settingsApi.get(),
  });

  useEffect(() => {
    if (settings && !formData) {
      setFormData(settings);
    }
  }, [settings, formData]);

  const saveMutation = useMutation({
    mutationFn: (request: UpdatePlatformSettingsRequest) => settingsApi.update(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platformSettings'] });
      setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  const testSlackMutation = useMutation({
    mutationFn: (webhookUrl: string) => settingsApi.testSlackWebhook(webhookUrl),
  });

  const handleChange = <K extends 'features' | 'security' | 'notifications'>(
    section: K,
    key: keyof PlatformSettings[K],
    value: unknown
  ) => {
    if (!formData) return;
    const sectionData = formData[section];
    setFormData({
      ...formData,
      [section]: {
        ...sectionData,
        [key]: value,
      },
    });
    setHasChanges(true);
  };

  const handleTopLevelChange = <K extends keyof PlatformSettings>(
    key: K,
    value: PlatformSettings[K]
  ) => {
    if (!formData) return;
    setFormData({
      ...formData,
      [key]: value,
    });
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!formData) return;
    saveMutation.mutate(formData);
  };

  const handleReset = () => {
    if (settings) {
      setFormData(settings);
      setHasChanges(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Spinner size="large" label="Loading settings..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card style={{ margin: '1rem', padding: '1rem' }}>
        <Body1 style={{ color: tokens.colorPaletteRedForeground1 }}>
          Error loading settings: {error instanceof Error ? error.message : 'Unknown error'}
        </Body1>
      </Card>
    );
  }

  if (!formData) return null;

  return (
    <div style={{ padding: '1.5rem', maxWidth: '900px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <SettingsRegular style={{ fontSize: '2rem' }} />
          <Title1>Platform Settings</Title1>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button
            appearance="secondary"
            icon={<ArrowResetRegular />}
            disabled={!hasChanges}
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button
            appearance="primary"
            icon={<SaveRegular />}
            disabled={!hasChanges || saveMutation.isPending}
            onClick={handleSave}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {saveSuccess && (
        <MessageBar intent="success" style={{ marginBottom: '1rem' }}>
          <MessageBarBody>
            <MessageBarTitle>Settings Saved</MessageBarTitle>
            Your changes have been saved successfully.
          </MessageBarBody>
        </MessageBar>
      )}

      {/* General Settings */}
      <Card style={{ marginBottom: '1rem' }}>
        <CardHeader
          image={<SettingsRegular style={{ fontSize: '1.5rem' }} />}
          header={<Title2>General Settings</Title2>}
          description={<Body2>Basic platform configuration</Body2>}
        />
        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Field label="Platform Name">
            <Input
              value={formData.platformName}
              onChange={(_, data) => handleTopLevelChange('platformName', data.value)}
            />
          </Field>
          <Field label="Support Email">
            <Input
              type="email"
              value={formData.supportEmail}
              onChange={(_, data) => handleTopLevelChange('supportEmail', data.value)}
            />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <Field label="Default Trial Duration (days)">
              <Input
                type="number"
                value={formData.defaultTrialDays.toString()}
                onChange={(_, data) => handleTopLevelChange('defaultTrialDays', parseInt(data.value) || 14)}
              />
            </Field>
            <Field label="Max Users Per Tenant">
              <Input
                type="number"
                value={formData.maxUsersPerTenant.toString()}
                onChange={(_, data) => handleTopLevelChange('maxUsersPerTenant', parseInt(data.value) || 100)}
              />
            </Field>
          </div>
        </div>
      </Card>

      {/* Feature Flags */}
      <Card style={{ marginBottom: '1rem' }}>
        <CardHeader
          image={<AlertRegular style={{ fontSize: '1.5rem' }} />}
          header={<Title2>Feature Flags</Title2>}
          description={<Body2>Enable or disable platform features</Body2>}
        />
        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Body1 style={{ fontWeight: 500 }}>Enable Trial Signups</Body1>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                Allow new customers to sign up for trial accounts
              </Body2>
            </div>
            <Switch
              checked={formData.features.enableTrialSignups}
              onChange={(_, data) => handleChange('features', 'enableTrialSignups', data.checked)}
            />
          </div>
          <Divider />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Body1 style={{ fontWeight: 500 }}>Self-Service Provisioning</Body1>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                Allow tenants to self-provision additional features
              </Body2>
            </div>
            <Switch
              checked={formData.features.enableSelfServiceProvisioning}
              onChange={(_, data) => handleChange('features', 'enableSelfServiceProvisioning', data.checked)}
            />
          </div>
          <Divider />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Body1 style={{ fontWeight: 500 }}>AI Narrator</Body1>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                Enable AI-powered security insights and recommendations
              </Body2>
            </div>
            <Switch
              checked={formData.features.enableAiNarrator}
              onChange={(_, data) => handleChange('features', 'enableAiNarrator', data.checked)}
            />
          </div>
          <Divider />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Body1 style={{ fontWeight: 500 }}>Advanced Compliance</Body1>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                Enable advanced compliance features (SOC 2, HIPAA, etc.)
              </Body2>
            </div>
            <Switch
              checked={formData.features.enableAdvancedCompliance}
              onChange={(_, data) => handleChange('features', 'enableAdvancedCompliance', data.checked)}
            />
          </div>
          <Divider />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Body1 style={{ fontWeight: 500, color: tokens.colorPaletteRedForeground1 }}>Maintenance Mode</Body1>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                Put the platform in maintenance mode (blocks all user access)
              </Body2>
            </div>
            <Switch
              checked={formData.features.maintenanceMode}
              onChange={(_, data) => handleChange('features', 'maintenanceMode', data.checked)}
            />
          </div>
          {formData.features.maintenanceMode && (
            <Field label="Maintenance Message">
              <Textarea
                value={formData.features.maintenanceMessage || ''}
                onChange={(_, data) => handleChange('features', 'maintenanceMessage', data.value)}
                placeholder="We're currently performing scheduled maintenance..."
                rows={3}
              />
            </Field>
          )}
        </div>
      </Card>

      {/* Security Settings */}
      <Card style={{ marginBottom: '1rem' }}>
        <CardHeader
          image={<ShieldLockRegular style={{ fontSize: '1.5rem' }} />}
          header={<Title2>Security Settings</Title2>}
          description={<Body2>Authentication and access control</Body2>}
        />
        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Body1 style={{ fontWeight: 500 }}>Require MFA</Body1>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                Require multi-factor authentication for all platform users
              </Body2>
            </div>
            <Switch
              checked={formData.security.requireMfa}
              onChange={(_, data) => handleChange('security', 'requireMfa', data.checked)}
            />
          </div>
          <Divider />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <Field label="Session Timeout (minutes)">
              <Input
                type="number"
                value={formData.security.sessionTimeoutMinutes.toString()}
                onChange={(_, data) => handleChange('security', 'sessionTimeoutMinutes', parseInt(data.value) || 60)}
              />
            </Field>
            <Field label="Max Login Attempts">
              <Input
                type="number"
                value={formData.security.maxLoginAttempts.toString()}
                onChange={(_, data) => handleChange('security', 'maxLoginAttempts', parseInt(data.value) || 5)}
              />
            </Field>
          </div>
          <Field label="Password Expiration (days)">
            <Input
              type="number"
              value={formData.security.passwordExpirationDays.toString()}
              onChange={(_, data) => handleChange('security', 'passwordExpirationDays', parseInt(data.value) || 90)}
            />
          </Field>
          <Field label="Allowed IP Ranges (CIDR notation, comma-separated)">
            <Textarea
              value={formData.security.allowedIpRanges.join(', ')}
              onChange={(_, data) => handleChange('security', 'allowedIpRanges', data.value.split(',').map(s => s.trim()).filter(Boolean))}
              placeholder="10.0.0.0/8, 192.168.1.0/24"
              rows={2}
            />
          </Field>
        </div>
      </Card>

      {/* Notification Settings */}
      <Card>
        <CardHeader
          image={<AlertRegular style={{ fontSize: '1.5rem' }} />}
          header={<Title2>Notification Settings</Title2>}
          description={<Body2>Configure platform notifications</Body2>}
        />
        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Body1 style={{ fontWeight: 500 }}>Email Notifications</Body1>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                Send email notifications for important events
              </Body2>
            </div>
            <Switch
              checked={formData.notifications.enableEmailNotifications}
              onChange={(_, data) => handleChange('notifications', 'enableEmailNotifications', data.checked)}
            />
          </div>
          <Divider />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Body1 style={{ fontWeight: 500 }}>Slack Notifications</Body1>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                Send notifications to a Slack channel
              </Body2>
            </div>
            <Switch
              checked={formData.notifications.enableSlackNotifications}
              onChange={(_, data) => handleChange('notifications', 'enableSlackNotifications', data.checked)}
            />
          </div>
          {formData.notifications.enableSlackNotifications && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
              <Field label="Slack Webhook URL" style={{ flex: 1 }}>
                <Input
                  value={formData.notifications.slackWebhookUrl || ''}
                  onChange={(_, data) => handleChange('notifications', 'slackWebhookUrl', data.value)}
                  placeholder="https://hooks.slack.com/services/..."
                />
              </Field>
              <Button
                appearance="secondary"
                onClick={() => formData.notifications.slackWebhookUrl && testSlackMutation.mutate(formData.notifications.slackWebhookUrl)}
                disabled={!formData.notifications.slackWebhookUrl || testSlackMutation.isPending}
              >
                {testSlackMutation.isPending ? 'Testing...' : 'Test'}
              </Button>
              {testSlackMutation.isSuccess && (
                <CheckmarkCircleRegular style={{ color: tokens.colorPaletteGreenForeground1, fontSize: '1.5rem' }} />
              )}
            </div>
          )}
          <Divider />
          <Body2 style={{ fontWeight: 600 }}>Notification Triggers</Body2>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Body1>Notify on new tenant creation</Body1>
            <Switch
              checked={formData.notifications.notifyOnNewTenant}
              onChange={(_, data) => handleChange('notifications', 'notifyOnNewTenant', data.checked)}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Body1>Notify on trial expiring</Body1>
            <Switch
              checked={formData.notifications.notifyOnTrialExpiring}
              onChange={(_, data) => handleChange('notifications', 'notifyOnTrialExpiring', data.checked)}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Body1>Notify on security alerts</Body1>
            <Switch
              checked={formData.notifications.notifyOnSecurityAlert}
              onChange={(_, data) => handleChange('notifications', 'notifyOnSecurityAlert', data.checked)}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};
